import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CountrySelect } from "@/components/CountrySelect";
import {
  isVerified,
  isEligible,
  setVerification,
  getMinimumAge,
} from "@/lib/age-verification";
import heroBg from "@/assets/hero-cigars-world.png";
import { Logo } from "@/components/Logo";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import { hasAccountDeclaration } from "@/lib/onboarding/fast-track";

type GateState = "form" | "eligible" | "denied";

export function AgeGate({ children }: { children: React.ReactNode }) {
  const [passed, setPassed] = useState(() => isVerified());
  const { user } = useAuth();
  // A signed-in member who already declared their age at sign-up (e.g. opening
  // the confirmation link on another browser) is not asked again.
  const accountDeclared = hasAccountDeclaration(user);

  if (passed || accountDeclared) return <>{children}</>;
  return <AgeGateScreen onPass={() => setPassed(true)} />;
}

function AgeGateScreen({ onPass }: { onPass: () => void }) {
  const { t } = useTranslation();
  const [country, setCountry] = useState<string | null>(null);
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [state, setState] = useState<GateState>("form");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const locale = navigator.language || "";
      const parts = locale.split("-");
      if (parts.length >= 2) {
        const regionCode = parts[parts.length - 1].toUpperCase();
        if (regionCode.length === 2) setCountry(regionCode);
      }
    } catch {
      // ignore
    }
  }, []);

  const validate = useCallback(() => {
    setError(null);

    if (!country) {
      setError(t("ageGate.errorSelectCountry"));
      return;
    }

    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);

    if (!d || !m || !y || year.length < 4) {
      setError(t("ageGate.errorInvalidDob"));
      return;
    }

    const dob = new Date(y, m - 1, d);
    if (
      isNaN(dob.getTime()) ||
      dob.getDate() !== d ||
      dob.getMonth() !== m - 1 ||
      dob.getFullYear() !== y
    ) {
      setError(t("ageGate.errorInvalidDob"));
      return;
    }

    if (dob > new Date()) {
      setError(t("ageGate.errorInvalidDob"));
      return;
    }

    if (isEligible(dob, country)) {
      setState("eligible");
    } else {
      setState("denied");
    }
  }, [country, day, month, year, t]);

  const handleEnter = () => {
    if (country) setVerification(country);
    onPass();
  };

  const handleLeave = () => {
    window.location.href = "https://www.google.com";
  };

  const minAge = country ? getMinimumAge(country) : 18;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto p-safe">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="absolute inset-0 bg-foreground/75 backdrop-blur-sm" />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4 my-6 md:my-8">
        <div className="rounded-2xl border border-border/20 bg-card/95 backdrop-blur-xl shadow-surface-lg p-6 md:p-10">
          <div className="flex justify-center mb-8">
            <Logo size="lg" />
          </div>

          {state === "form" && (
            <FormView
              country={country}
              setCountry={setCountry}
              day={day}
              setDay={setDay}
              month={month}
              setMonth={setMonth}
              year={year}
              setYear={setYear}
              error={error}
              onValidate={validate}
              onLeave={handleLeave}
            />
          )}

          {state === "eligible" && (
            <EligibleView onEnter={handleEnter} />
          )}

          {state === "denied" && (
            <DeniedView minAge={minAge} onLeave={handleLeave} />
          )}

          <p className="text-[10px] text-muted-foreground/70 text-center mt-8 leading-relaxed">
            {t("ageGate.footer")}
          </p>
        </div>

        {state === "form" && (
          <p className="text-[10px] text-muted-foreground/50 text-center mt-4">
            {t("ageGate.privacyNote")}
          </p>
        )}
      </div>
    </div>
  );
}

function FormView({
  country, setCountry, day, setDay, month, setMonth, year, setYear, error, onValidate, onLeave,
}: {
  country: string | null; setCountry: (v: string | null) => void;
  day: string; setDay: (v: string) => void;
  month: string; setMonth: (v: string) => void;
  year: string; setYear: (v: string) => void;
  error: string | null; onValidate: () => void; onLeave: () => void;
}) {
  const { t } = useTranslation();
  const handleNumInput = (value: string, max: number, setter: (v: string) => void) => {
    const cleaned = value.replace(/\D/g, "").slice(0, max);
    setter(cleaned);
  };

  return (
    <>
      <div className="text-center mb-6">
        <h2 className="font-display text-lg md:text-xl font-semibold text-foreground">
          {t("ageGate.title")}
        </h2>
        <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mt-1 font-medium">
          {t("ageGate.subtitle")}
        </p>
      </div>

      <p className="text-sm text-muted-foreground text-center leading-relaxed mb-6">
        {t("ageGate.description")}
      </p>

      <p className="text-xs text-muted-foreground text-center mb-5">
        {t("ageGate.confirmPrompt")}
      </p>

      <div className="space-y-1.5 mb-4">
        <label className="text-xs font-medium text-foreground">{t("ageGate.countryLabel")}</label>
        <CountrySelect value={country} onChange={setCountry} placeholder={t("ageGate.countryPlaceholder")} />
      </div>

      <div className="space-y-1.5 mb-5">
        <label className="text-xs font-medium text-foreground">{t("ageGate.dobLabel")}</label>
        <div className="grid grid-cols-3 gap-2">
          <Input placeholder="DD" inputMode="numeric" value={day} onChange={(e) => handleNumInput(e.target.value, 2, setDay)} className="text-center text-sm" aria-label="Day" />
          <Input placeholder="MM" inputMode="numeric" value={month} onChange={(e) => handleNumInput(e.target.value, 2, setMonth)} className="text-center text-sm" aria-label="Month" />
          <Input placeholder="YYYY" inputMode="numeric" value={year} onChange={(e) => handleNumInput(e.target.value, 4, setYear)} className="text-center text-sm" aria-label="Year" />
        </div>
      </div>

      {error && (
        <p className="text-xs text-destructive text-center mb-4" role="alert">{error}</p>
      )}

      <div className="text-[11px] text-muted-foreground leading-relaxed mb-6 space-y-1">
        <p>{t("ageGate.complianceIntro")}</p>
        <ul className="list-disc list-inside space-y-0.5 pl-1">
          <li>{t("ageGate.complianceLegalAge")}</li>
          <li>{t("ageGate.complianceLocation")}</li>
          <li>{t("ageGate.complianceAccuracy")}</li>
        </ul>
      </div>

      <Button onClick={onValidate} className="w-full" size="lg">{t("common.continue")}</Button>

      <button onClick={onLeave} className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors py-2">
        {t("common.leaveSite")}
      </button>
    </>
  );
}

function EligibleView({ onEnter }: { onEnter: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="text-center">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
        <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="font-display text-lg font-semibold text-foreground mb-2">{t("ageGate.welcomeTitle")}</h2>
      <p className="text-sm text-muted-foreground mb-8">{t("ageGate.welcomeMessage")}</p>
      <Button onClick={onEnter} className="w-full" size="lg">{t("common.enterSite")}</Button>
    </div>
  );
}

function DeniedView({ minAge, onLeave }: { minAge: number; onLeave: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="text-center">
      <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-5">
        <svg className="w-6 h-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h2 className="font-display text-lg font-semibold text-foreground mb-2">{t("ageGate.deniedTitle")}</h2>
      <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
        {t("ageGate.deniedMessage", { minAge })}
      </p>
      <Button onClick={onLeave} variant="outline" className="w-full" size="lg">{t("common.leaveSite")}</Button>
    </div>
  );
}
